// Reading Archetype System for Onboarding

export type Archetype = 'Explorer' | 'Thinker' | 'Dreamer' | 'Connector' | 'Escapist' | 'Archivist';

export type OnboardingAnswers = {
  mood: string;
  growth: string;
  goal: number;
  weeklyGoal: number;
};

const codeToName: Record<string, Archetype> = {
  EX: 'Explorer',
  TH: 'Thinker',
  DR: 'Dreamer',
  CO: 'Connector',
  ES: 'Escapist',
  AR: 'Archivist'
};

export function computeArchetype(a: OnboardingAnswers): {
  archetype: Archetype;
  scores: Record<Archetype, number>;
} {
  const S: Record<string, number> = { EX: 0, TH: 0, DR: 0, CO: 0, ES: 0, AR: 0 };

  // Helper to add points
  const add = (code: string, n = 1) => {
    S[code] += n;
  };

  // A) mood
  switch (a.mood) {
    case 'Escape into another world':
      add('ES', 2);
      add('DR', 1);
      break;
    case 'Sharpen my mind':
      add('TH', 3);
      break;
    case 'Feel all the feels':
      add('DR', 3);
      break;
    case 'Laugh or be entertained':
      add('ES', 2);
      add('CO', 1);
      break;
    case 'Surprise me':
      add('EX', 3);
      break;
  }

  // B) growth
  switch (a.growth) {
    case 'Read more books overall':
      add('AR', 2);
      add('TH', 1);
      break;
    case 'Explore new genres':
      add('EX', 3);
      break;
    case 'Spend more time daily':
      add('AR', 2);
      break;
    case 'Get back into the habit':
      add('ES', 1);
      add('DR', 1);
      break;
    case 'Connect with friends through books':
      add('CO', 3);
      break;
  }

  // C) goal (numeric buckets)
  if (a.goal <= 8) {
    add('DR', 1);
    add('ES', 1);
  } else if (a.goal <= 18) {
    add('AR', 1);
  } else if (a.goal <= 36) {
    add('AR', 2);
  } else {
    add('AR', 2);
    add('TH', 1);
  }

  // D) weeklyGoal (commitment level)
  if (a.weeklyGoal >= 6) {
    add('AR', 2); // High commitment = Archivist
  } else if (a.weeklyGoal >= 4) {
    add('AR', 1);
  }

  // Resolve winner
  const max = Math.max(...Object.values(S));
  let winners = Object.entries(S)
    .filter(([_, v]) => v === max)
    .map(([k]) => k as keyof typeof S);

  if (winners.length > 1) {
    // Tie-break #1: mood priority
    const moodPref: Record<string, string> = {
      'Surprise me': 'EX',
      'Sharpen my mind': 'TH',
      'Feel all the feels': 'DR',
      'Laugh or be entertained': 'ES',
      'Escape into another world': 'ES'
    };
    const mChoice = moodPref[a.mood];
    if (mChoice && winners.includes(mChoice)) {
      winners = [mChoice as keyof typeof S];
    }

    // Tie-break #2: growth preference
    if (winners.length > 1) {
      const growthPref: Record<string, string[]> = {
        'Read more books overall': ['AR'],
        'Explore new genres': ['EX'],
        'Spend more time daily': ['AR'],
        'Get back into the habit': ['DR', 'ES'],
        'Connect with friends through books': ['CO']
      };
      const opts = growthPref[a.growth] || [];
      const pick = opts.find((c) => winners.includes(c));
      if (pick) winners = [pick as keyof typeof S];
    }

    // Final fallback: stable order
    if (winners.length > 1) {
      const order = ['AR', 'EX', 'TH', 'DR', 'CO', 'ES'];
      winners = [order.find((c) => winners.includes(c)) as keyof typeof S];
    }
  }

  const winner = codeToName[winners[0]];
  const scores: Record<Archetype, number> = {
    Explorer: S.EX,
    Thinker: S.TH,
    Dreamer: S.DR,
    Connector: S.CO,
    Escapist: S.ES,
    Archivist: S.AR
  };

  return { archetype: winner, scores };
}

// Archetype descriptions and CTAs
export const archetypeInfo: Record<Archetype, { description: string; cta: string; emoji: string }> = {
  Explorer: {
    description: "You love discovering hidden gems and venturing into new literary territories.",
    cta: "Discover hidden gems from readers like you",
    emoji: "🗺️"
  },
  Thinker: {
    description: "You seek books that challenge your mind and expand your understanding.",
    cta: "Queue up smart, idea-rich reads",
    emoji: "🧠"
  },
  Dreamer: {
    description: "You escape into stories that touch your heart and spark your imagination.",
    cta: "Build a cozy bedtime reading streak",
    emoji: "💭"
  },
  Connector: {
    description: "You love sharing book experiences and connecting with fellow readers.",
    cta: "Follow friends & join a lively chat",
    emoji: "🤝"
  },
  Escapist: {
    description: "You dive into fast-paced stories that transport you to other worlds.",
    cta: "One-tap fast-paced recommendations",
    emoji: "🚀"
  },
  Archivist: {
    description: "You track every book, celebrate milestones, and love seeing your progress.",
    cta: "Track stats, streaks & milestones",
    emoji: "📊"
  }
};
